/**
 * Card-level fitment guard — stop one-click Add for a part that cannot fit the
 * truck the shopper has already told us about.
 *
 * THE DEFECT. Codex third-pass audit P0-02, confirmed live 2026-08-28 with
 * `Ram · 2019 · 2500` saved in the garage. The homepage offered direct Add to
 * cart for:
 *   hd-rear-shock-guards-2014-26-ram-3500              model ["3500"]
 *   def-transfer-case-guard-2025-26-2500-3500-cummins  year  ["2025","2026"]
 * The second is a YEAR mismatch — a 2019 owner one click from a part built for a
 * truck six model years newer.
 *
 * WHY THE EXISTING GUARD MISSED THEM. snippets/quick-add.liquid blocks direct Add
 * for products that carry an option NAMED like a fitment field (year, engine,
 * model, chassis…). Both of these are single-variant products whose fitment lives
 * in the title and the metafields, so they have no such option and walked past it.
 * That guard is about options; this one is about the saved vehicle. They are
 * different questions and both are needed.
 *
 * ⚠ FAIL OPEN. THIS IS THE WHOLE SAFETY ARGUMENT, so it is stated before the code.
 * The button is only ever swapped on a DEFINITE contradiction: the garage holds a
 * value for a field, the product declares that same field, the declared list is
 * non-empty, and the saved value is not in it. Every other state — no garage, no
 * metafields, an unparseable attribute, a field the product does not declare, an
 * empty list — leaves Add to cart exactly as it was. A guard on a buy path two
 * days before cutover must not be able to block a sale that would have completed.
 * The worst it can do is miss a case.
 *
 * AND IT NEVER REMOVES A ROUTE TO PURCHASE. The swap is Add to cart -> a link to
 * the product page reading "Check compatibility". Nothing is disabled, nothing is
 * hidden, and the customer is one click from buying it anyway if they know
 * better than the data. That matters: the fitment metafields are Marcus's and
 * three products still carry a 2003-2027 universal default.
 *
 * IT IS EXACTLY AS GOOD AS WHAT THE SHOPPER TOLD THE SELECTOR, which is worth
 * understanding before reading a result. "Power Wagon" is not a value of `model`
 * in this catalogue — the 3-Bar Skid Plate is model ["2500"], and its Power Wagon
 * specificity lives in engine ["6.4L HEMI"]. So:
 *
 *   saved year/make/model only        -> the 3-Bar plate is NOT flagged. Nothing
 *                                        about a 2019 Ram 2500 contradicts it.
 *   saved engine as well              -> a Cummins truck flags it on engine, and
 *                                        so does the HEMI evap guard for a
 *                                        Cummins, and both DEF guards for a HEMI.
 *
 * Measured on /collections/ram-hd-skid-plates 2026-08-28, both directions:
 *   2019 · 2500 · 6.4L HEMI   flags the 2025-26 Cummins guard (year, engine), the
 *                             3500 shock guards (model), the 2014-24 Cummins DEF
 *                             guard (engine). Leaves the Power Wagon plate, the
 *                             HEMI evap guard and the 2500 shock guards alone.
 *   2019 · 2500 · 6.7L Cummins flags the 2025-26 guard (year), the 3500 shock
 *                             guards (model), the HEMI evap guard (engine) and
 *                             the Power Wagon plate (engine). Leaves the 2014-24
 *                             Cummins DEF guard alone — the part this truck wants.
 *
 * Engine is in the selector's primary row, not behind "More options", so a garage
 * saved through the normal flow carries it. All three of the audit's examples are
 * caught by a garage from that flow.
 */

const STORE_KEY = 'dethloff.garage';

/* Values come from two places — a <select> in the vehicle selector and a Shopify
 * metafield list — and both are merchant-typed. Compare on trimmed lower case so
 * "Crew Cab" and "crew cab" agree, but do NOT strip punctuation: bed lengths are
 * 6'4" and 8', and normalising quotes away would make those two collide. */
const norm = (value) => String(value == null ? '' : value).trim().toLowerCase();

function readGarage() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    /* Private mode, disabled storage, or a corrupted value. Fail open. */
    return null;
  }
}

/**
 * Which saved fields this product's own data contradicts. Empty array means no
 * conflict, and that includes every "we cannot tell" case.
 */
function conflictingFields(fitment, garage) {
  const conflicts = [];
  for (const [key, saved] of Object.entries(garage)) {
    if (!saved) continue;
    const allowed = fitment[key];
    if (!Array.isArray(allowed) || allowed.length === 0) continue;
    if (!allowed.some((value) => norm(value) === norm(saved))) conflicts.push(key);
  }
  return conflicts;
}

const LABELS = {
  year: 'year',
  make: 'make',
  model: 'model',
  engine: 'engine',
  cab: 'cab',
  bed_length: 'bed length',
  box_type: 'box type',
  body_style: 'body style',
};

/**
 * Wording comes from Theme settings > Fitment warnings, handed in on the link as
 * `data-dm-conflict-note`. A sentence a customer reads should not need a developer.
 * The fallback is the original string so a card from an older template still says
 * something sensible. [[conflicts]] is the only placeholder.
 */
const DEFAULT_NOTE = 'This part is not listed for your saved [[conflicts]]. Open the product page to check.';

function describe(conflicts, garage, template) {
  const parts = conflicts.slice(0, 2).map((key) => `${LABELS[key] || key} ${garage[key]}`);
  return (template || DEFAULT_NOTE).split('[[conflicts]]').join(parts.join(' and '));
}

function guard(element) {
  if (element.dataset.dmFitmentChecked === '1') return;
  element.dataset.dmFitmentChecked = '1';

  const form = element.querySelector('.quick-add__product-form-component');
  const link = element.querySelector('[data-dm-check-fitment]');
  if (!form || !link) return;

  let fitment;
  try {
    fitment = JSON.parse(element.dataset.dmFitment || 'null');
  } catch {
    return;
  }
  if (!fitment || typeof fitment !== 'object') return;

  const garage = readGarage();
  if (!garage) return;

  const conflicts = conflictingFields(fitment, garage);
  if (conflicts.length === 0) return;

  link.title = describe(conflicts, garage, link.dataset.dmConflictNote);
  link.hidden = false;
  form.hidden = true;

  /* ⚠ CLEARING THE hidden ATTRIBUTE IS NOT ENOUGH, AND THIS IS THE HALF THAT WAS
   * WRONG THE FIRST TIME. snippets/quick-add-styles.liquid carries
   *     [data-quick-add-button='add'] .quick-add__button--choose { display: none }
   * so with the card still in 'add' mode the link measured 0x0 with a computed
   * display of none — present, unhidden, and invisible. Caught by measuring it in
   * a browser; the markup and the files looked perfect.
   *
   * Flipping the card to 'choose' uses the theme's OWN mode switch instead of
   * fighting it with a competing override: that same rule set then hides the
   * add-to-cart component and reveals anything carrying --choose, which this link
   * does. The form.hidden above stays as the belt — a CSS rule can be
   * out-specified, an attribute cannot. Safe on these products because they render
   * no real Choose button (data-renders-both-buttons is empty), so nothing flips
   * the mode back underneath us. */
  element.dataset.quickAddButton = 'choose';
  element.dataset.dmFitmentBlocked = conflicts.join(',');
}

function sweep(root) {
  const scope = root instanceof Element || root instanceof Document ? root : document;
  scope.querySelectorAll('quick-add-component[data-dm-fitment]').forEach(guard);
}

sweep(document);

/* Cards arrive after load in three ways on this store: infinite scroll on
 * collection and search, the product-recommendations section fetching its own
 * markup, and the theme editor re-rendering a section. One observer covers all
 * three; per-surface hooks would miss whichever one is added next. */
if (typeof MutationObserver === 'function') {
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches?.('quick-add-component[data-dm-fitment]')) guard(node);
        else sweep(node);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
}

document.addEventListener('shopify:section:load', (event) => sweep(event.target));
